import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CreditCard, CheckCircle, Loader2, Building2, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SemiTruck from "@/components/icons/SemiTruck";
import SquarePaymentForm from "@/components/SquarePaymentForm";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicPayInvoice() {
  const { id, businessSlug } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [error, setError] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [squareConfig, setSquareConfig] = useState(null);
  const [activeProvider, setActiveProvider] = useState(null); // 'stripe' | 'square' | null
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  const API = `${BASE_API}/b/${businessSlug}`;

  const fetchData = useCallback(async () => {
    try {
      const [invoiceRes, settingsRes, businessRes, squareConfigRes, providerRes] = await Promise.all([
        axios.get(`${API}/invoices/${id}`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/info`),
        axios.get(`${API}/payments/square-config`).catch(() => ({ data: { enabled: false } })),
        axios.get(`${API}/payments/active-provider`).catch(() => ({ data: { provider: null } })),
      ]);
      setInvoice(invoiceRes.data);
      setSettings(settingsRes.data);
      setBusinessInfo(businessRes.data);
      setSquareConfig(squareConfigRes.data);
      setActiveProvider(providerRes.data?.provider || null);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setError("Invoice not found or has been removed.");
    } finally {
      setLoading(false);
    }
  }, [id, API]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleCardPayment = async (sourceId) => {
    setProcessingPayment(true);
    try {
      const balanceDue = invoice.total - (invoice.amount_paid || 0);
      const response = await axios.post(`${API}/payments/square-web`, {
        invoice_id: id,
        source_id: sourceId,
        amount: balanceDue
      });
      
      if (response.data.success) {
        setPaymentSuccess(true);
        setShowPaymentForm(false);
        // Refresh invoice data
        fetchData();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Payment failed. Please try again.";
      setError(errorMessage);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handlePayWithCard = async () => {
    // Route to whichever provider is actually connected for this business.
    // Stripe Checkout takes precedence (it's the modern flow and supports
    // Apple Pay / Google Pay out of the box). Falls back to Square's
    // embedded form / redirect for legacy merchants. If nothing is
    // connected, surface a friendly message instead of silently failing.
    setError(null);

    if (activeProvider === 'stripe') {
      setProcessingPayment(true);
      try {
        const response = await axios.post(`${API}/payments/stripe/checkout`, {
          invoice_id: id,
          origin: window.location.origin,
        });
        const checkoutUrl = response.data?.url;
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
          return;
        }
        throw new Error('No checkout URL received');
      } catch (error) {
        console.error('Stripe payment error:', error);
        setError(error?.response?.data?.detail || 'Unable to start card payment. Please try Zelle below or contact the business.');
        setProcessingPayment(false);
      }
      return;
    }

    if (activeProvider === 'square') {
      // Embedded Square Web Payments form when available.
      if (squareConfig?.enabled && squareConfig?.application_id) {
        setShowPaymentForm(true);
        return;
      }
      // Otherwise Square checkout redirect.
      setProcessingPayment(true);
      try {
        const response = await axios.post(`${API}/payments/checkout`, {
          invoice_id: id,
          origin_url: window.location.origin,
        });
        const checkoutUrl = response.data.url || response.data.checkout_url;
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
          return;
        }
        throw new Error('No checkout URL received');
      } catch (error) {
        console.error('Square payment error:', error);
        setError(error?.response?.data?.detail || 'Unable to process payment. Please try Zelle below or contact the business.');
        setProcessingPayment(false);
      }
      return;
    }

    // No provider connected.
    setError("Online card payments aren't set up for this business yet. Please use Zelle below or contact the business directly.");
  };

  // Calculate totals
  const calculateTotals = () => {
    if (!invoice) return { paid: 0, balance: 0 };
    const payments = invoice.payments || [];
    const totalPaid = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const balance = (invoice.total || 0) - totalPaid;
    return { paid: totalPaid, balance: Math.max(0, balance) };
  };

  const { paid, balance } = calculateTotals();
  const isPaid = invoice?.payment_status === 'paid' || balance <= 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
          <p className="mt-2 text-slate-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Invoice Not Found</h2>
            <p className="text-slate-500">{error || "This invoice may have been removed or the link is invalid."}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const zelle_email = settings?.zelle_info?.zelle_email || businessInfo?.email || '';
  const zelle_phone = settings?.zelle_info?.zelle_phone || businessInfo?.phone || '';

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="public-pay-invoice">
      <div className="max-w-3xl mx-auto">
        {/* Business Header */}
        <div className="bg-white rounded-t-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {businessInfo?.logo_url ? (
                <img 
                  src={businessInfo.logo_url} 
                  alt={businessInfo?.name}
                  className="w-20 h-auto max-h-20 object-contain"
                />
              ) : (
                <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center">
                  <SemiTruck className="w-10 h-10 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-slate-800">
                  {businessInfo?.name || settings?.business_info?.name || 'Business'}
                </h1>
                {businessInfo?.address && (
                  <p className="text-slate-500 flex items-center gap-1 mt-1">
                    <MapPin className="w-4 h-4" />
                    {businessInfo.address}
                  </p>
                )}
                <div className="flex gap-4 mt-1 text-sm text-slate-500">
                  {businessInfo?.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {businessInfo.phone}
                    </span>
                  )}
                  {businessInfo?.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {businessInfo.email}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Invoice</p>
              <p className="text-xl font-bold text-slate-800">{invoice.invoice_number}</p>
              <p className="text-sm text-slate-500">{formatDate(invoice.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="bg-white border-x border-slate-200 p-6">
          {/* Bill To */}
          <div className="mb-6 pb-6 border-b border-slate-200">
            <p className="text-sm text-slate-500 mb-1">Bill To</p>
            <p className="font-semibold text-slate-800">{invoice.customer_name}</p>
            {invoice.company_name && <p className="text-slate-600">{invoice.company_name}</p>}
            {invoice.customer_phone && <p className="text-slate-500">{invoice.customer_phone}</p>}
            {invoice.customer_email && <p className="text-slate-500">{invoice.customer_email}</p>}
          </div>

          {/* Vehicle Info */}
          {(invoice.vehicle_info || invoice.truck_number) && (
            <div className="mb-6 pb-6 border-b border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Vehicle Information</p>
              {invoice.vehicle_info && <p className="text-slate-700">{invoice.vehicle_info}</p>}
              {invoice.truck_number && <p className="text-slate-600">Truck #: {invoice.truck_number}</p>}
            </div>
          )}

          {/* Items Table */}
          <div className="mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 text-slate-500 font-medium">Description</th>
                  <th className="text-center py-2 text-slate-500 font-medium">Qty</th>
                  <th className="text-right py-2 text-slate-500 font-medium">Price</th>
                  <th className="text-right py-2 text-slate-500 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items?.map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-100">
                    <td className="py-3 text-slate-700">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs mr-2 ${
                        item.type === 'part' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {item.type === 'part' ? 'Part' : 'Labor'}
                      </span>
                      {item.description}
                    </td>
                    <td className="py-3 text-center text-slate-600">{item.quantity}</td>
                    <td className="py-3 text-right text-slate-600">{formatCurrency(item.unit_price)}</td>
                    <td className="py-3 text-right text-slate-700 font-medium">
                      {formatCurrency(item.quantity * item.unit_price)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span>
                <span>{formatCurrency(invoice.subtotal)}</span>
              </div>
              {invoice.tax_amount > 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>Tax ({(invoice.tax_rate * 100).toFixed(1)}%)</span>
                  <span>{formatCurrency(invoice.tax_amount)}</span>
                </div>
              )}
              {invoice.tax_exempt && (
                <div className="flex justify-between text-green-600">
                  <span>Tax Exempt</span>
                  <span>✓</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold text-slate-800 pt-2 border-t border-slate-200">
                <span>Total</span>
                <span>{formatCurrency(invoice.total)}</span>
              </div>
              {paid > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Paid</span>
                  <span>-{formatCurrency(paid)}</span>
                </div>
              )}
              {!isPaid && (
                <div className="flex justify-between text-xl font-bold text-blue-600 pt-2 border-t border-slate-200">
                  <span>Balance Due</span>
                  <span>{formatCurrency(balance)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Section */}
        <div className="bg-white rounded-b-xl shadow-sm border border-t-0 border-slate-200 p-6">
          {isPaid || paymentSuccess ? (
            <div className="text-center py-6 bg-green-50 rounded-lg">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-green-700">Paid in Full</h3>
              <p className="text-green-600">Thank you for your payment!</p>
            </div>
          ) : showPaymentForm && squareConfig?.enabled ? (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-1">Enter Card Details</h3>
                <p className="text-slate-500">Amount: <span className="font-bold text-blue-600">{formatCurrency(balance)}</span></p>
              </div>
              
              <SquarePaymentForm
                applicationId={squareConfig.application_id}
                locationId={squareConfig.location_id}
                environment={squareConfig.environment}
                amount={balance}
                isDark={false}
                onPaymentSuccess={handleCardPayment}
                onPaymentError={(err) => setError(err)}
              />
              
              <Button
                variant="outline"
                onClick={() => setShowPaymentForm(false)}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Pay Your Invoice</h3>
                <p className="text-slate-500">Choose your preferred payment method</p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              {/* Pay with Card Button — only when a provider is connected */}
              {activeProvider ? (
                <Button
                  onClick={handlePayWithCard}
                  disabled={processingPayment}
                  className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold"
                  data-testid="pay-with-card-btn"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay {formatCurrency(balance)} with Card
                    </>
                  )}
                </Button>
              ) : (
                <div
                  className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm text-center"
                  data-testid="no-card-payment-notice"
                >
                  Online card payments aren't set up for this business yet.
                  Please use <strong>Zelle</strong> below or contact the business directly.
                </div>
              )}

              {/* Zelle Option */}
              {(zelle_email || zelle_phone) && (
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-2">Or pay with <strong>Zelle</strong> (no processing fee)</p>
                  {zelle_phone && (
                    <p className="text-slate-700 font-medium">{zelle_phone}</p>
                  )}
                  {zelle_email && (
                    <p className="text-slate-700 font-medium">{zelle_email}</p>
                  )}
                  <p className="text-xs text-slate-400 mt-2">
                    Reference: {invoice.invoice_number}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-slate-500 mt-6 space-y-1">
          <p>Questions about this invoice? Contact {businessInfo?.name || 'us'} at {businessInfo?.phone || businessInfo?.email || ''}</p>
          <p>Need help? <a href="mailto:support@mybusinessportal.info" className="text-blue-500 hover:underline">support@mybusinessportal.info</a></p>
        </div>
      </div>
    </div>
  );
}
